// The statute gap, as work rather than as a number.
//
// Measured on the live book: of 166 open matters, 5 had a computable
// statute and 107 had a date of incident but no state. A dashboard tile
// reading "161 matters have no statute" is true, unactionable, and
// therefore ignored. What clears it is a list, sorted so the matters
// that need one keystroke come first, with the five states the firm
// works in as buttons.
//
// The ordering is the whole design. Matters needing ONLY a state lead,
// because those are the ones a person can finish; burying them under
// matters that also need a date of incident is how a worklist stops
// being used.
//
// No severity colours. A matter without a statute is not "red" — it is
// a matter somebody has to look up, and inventing a colour scale would
// imply a threshold nobody set.

import React, { useCallback, useEffect, useState } from "react";
import { C } from "../constants.js";
import { apiFetch } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";

const BUCKET_LABEL = {
  needs_state: "Needs the state of loss",
  needs_both: "Needs a state and a date of incident",
  needs_date: "Needs a date of incident",
  ready: "Ready to compute",
  has_deadlines: "Statute tracked",
};

export function StatuteGapsPage({ showNotification, onMatterClick }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState({});
  const [computing, setComputing] = useState(false);
  const [filter, setFilter] = useState("needs_state");

  const load = useCallback(async () => {
    try {
      setData(await apiFetch("/api/statute-gaps"));
    } catch (e) {
      setData({ _error: e.message });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setState = async (row, code) => {
    setSaving(s => ({ ...s, [row.matter_id]: true }));
    try {
      await apiFetch(`/api/matters/${row.matter_id}/loss-facts`, {
        method: "PUT",
        body: JSON.stringify({ state_of_loss: code, source: "manual" }),
      });
      // Updated in place rather than reloading the whole book: the
      // list is ~166 rows behind a Clio call, and re-fetching after
      // every keystroke would make clearing 100 matters unbearable.
      setData(d => {
        const rows = d.data.map(r => r.matter_id === row.matter_id
          ? { ...r, state_of_loss: code, state_source: "app",
              bucket: r.incident_date ? "ready" : "needs_date" }
          : r);
        return { ...d, data: rows, counts: recount(rows) };
      });
    } catch (e) {
      showNotification?.(e.message, "error");
    }
    setSaving(s => ({ ...s, [row.matter_id]: false }));
  };

  const recount = (rows) => {
    const c = { ready: 0, needs_state: 0, needs_date: 0,
                needs_both: 0, has_deadlines: 0 };
    rows.forEach(r => { c[r.bucket] = (c[r.bucket] || 0) + 1; });
    return c;
  };

  const computeAll = async () => {
    const ready = (data.data || []).filter(r => r.bucket === "ready");
    if (!ready.length) return;
    if (!window.confirm(
      `Compute statutory deadlines for ${ready.length} matter` +
      `${ready.length === 1 ? "" : "s"}?\n\n` +
      `Each one gets a statute of limitations and any notice-of-claim ` +
      `deadlines its state requires. Existing deadlines are updated, ` +
      `not duplicated, and anything already completed is left alone.`)) return;

    setComputing(true);
    let done = 0, failed = [];
    for (const r of ready) {
      try {
        await apiFetch(`/api/matters/${r.matter_id}/sol/compute`, {
          method: "POST",
          body: JSON.stringify({
            write_custom_field: false,      // never writes back to Clio
            write_sol_tracker: false,
            create_calendar_reminders: false,
          }),
        });
        done++;
      } catch (e) {
        // Named, not counted. "3 failed" is not something anyone can
        // act on; a list of matter numbers is.
        failed.push(`${r.display_number || r.matter_id}: ${e.message}`);
      }
    }
    setComputing(false);
    showNotification?.(
      failed.length
        ? `${done} computed. ${failed.length} could not be: ${failed.slice(0, 3).join("; ")}`
        : `${done} matters now have statutory deadlines.`,
      failed.length ? "error" : "success");
    await load();
  };

  if (!data) return React.createElement(Spinner);
  if (data._error) {
    return React.createElement("div", { style: { padding: "32px 40px" } },
      React.createElement("p", { style: { color: C.navy } }, data._error));
  }

  const counts = data.counts || {};
  const states = (data.states_of_practice || []).filter(Boolean);
  const rows = (data.data || []).filter(
    r => filter === "all" ? true : r.bucket === filter);

  const th = {
    textAlign: "left", fontSize: "11px", letterSpacing: "0.06em",
    color: C.textMuted, fontWeight: 600, padding: "0 10px 8px 0",
    textTransform: "uppercase",
  };
  const td = {
    padding: "9px 10px 9px 0", fontSize: "13px", color: C.navy,
    borderTop: `1px solid ${C.warmGray}`, verticalAlign: "middle",
  };

  return React.createElement("div",
    { className: "fade-in", style: { padding: "32px 40px", maxWidth: "1280px" } },

    React.createElement("h1", {
      style: {
        fontFamily: "'Playfair Display',serif", fontSize: "28px",
        fontWeight: 600, color: C.navy, marginBottom: "6px",
      },
    }, "Statute tracking"),
    React.createElement("p", {
      style: { fontSize: "15px", color: C.textMuted, marginBottom: "20px" },
    }, "A statute is computed from two facts: the date of the incident and "
     + "the state the loss happened in. This is every open matter, and which "
     + "of the two it is missing."),

    // The counts, as filters. A number you cannot open is a number
    // nobody acts on.
    React.createElement("div", {
      style: { display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "22px" },
    },
      [["needs_state", counts.needs_state], ["needs_both", counts.needs_both],
       ["needs_date", counts.needs_date], ["ready", counts.ready],
       ["has_deadlines", counts.has_deadlines], ["all", data.total],
      ].map(([key, n]) => React.createElement("button", {
        key,
        onClick: () => setFilter(key),
        style: {
          padding: "10px 14px", borderRadius: "8px", cursor: "pointer",
          background: filter === key ? C.navy : "#fff",
          color: filter === key ? "#fff" : C.navy,
          border: `1px solid ${filter === key ? C.navy : C.warmGray}`,
          fontSize: "13px", textAlign: "left", minWidth: "150px",
        },
      },
        React.createElement("div", {
          style: { fontSize: "20px", fontWeight: 600 },
        }, n ?? 0),
        React.createElement("div", {
          style: { fontSize: "12px", opacity: 0.85 },
        }, key === "all" ? "All open matters" : BUCKET_LABEL[key]),
      ))),

    counts.ready > 0 && React.createElement("div", {
      style: {
        background: "#fff", border: `1px solid ${C.warmGray}`,
        borderRadius: "8px", padding: "16px 20px", marginBottom: "20px",
        display: "flex", alignItems: "center", gap: "16px",
      },
    },
      React.createElement("div", { style: { flex: 1, fontSize: "13px", color: C.navy } },
        `${counts.ready} matter${counts.ready === 1 ? " has" : "s have"} both facts `
        + `and no statute recorded yet.`),
      React.createElement("button", {
        onClick: computeAll, disabled: computing,
        style: {
          padding: "9px 16px", fontSize: "13px", cursor: "pointer",
          background: C.navy, color: "#fff", border: "none", borderRadius: "6px",
        },
      }, computing ? "Computing…" : `Compute ${counts.ready}`),
    ),

    rows.length === 0
      ? React.createElement("p", { style: { fontSize: "14px", color: C.textMuted } },
          "Nothing in this group.")
      : React.createElement("div", {
          style: {
            background: "#fff", border: `1px solid ${C.warmGray}`,
            borderRadius: "8px", padding: "16px 20px", overflowX: "auto",
          },
        },
        React.createElement("table", { style: { width: "100%", borderCollapse: "collapse" } },
          React.createElement("thead", null,
            React.createElement("tr", null,
              React.createElement("th", { style: th }, "Matter"),
              React.createElement("th", { style: th }, "Client"),
              React.createElement("th", { style: th }, "Attorney"),
              React.createElement("th", { style: th }, "Incident"),
              React.createElement("th", { style: th }, "State of loss"),
            )),
          React.createElement("tbody", null,
            rows.map(r => React.createElement("tr", { key: r.matter_id },
              React.createElement("td", { style: td },
                React.createElement("button", {
                  onClick: () => onMatterClick && onMatterClick(r.matter_id),
                  style: {
                    border: "none", background: "none", padding: 0,
                    cursor: "pointer", color: C.navy, fontSize: "13px",
                    textDecoration: "underline", textDecorationColor: C.warmGray,
                  },
                }, r.display_number || r.matter_id)),
              React.createElement("td", { style: { ...td, color: C.textMuted } },
                r.client || "—"),
              React.createElement("td", { style: { ...td, color: C.textMuted } },
                r.attorney || "Unassigned"),
              React.createElement("td", {
                style: { ...td, color: r.incident_date ? C.navy : C.textMuted },
              }, r.incident_date || "not recorded"),
              React.createElement("td", { style: td },
                r.state_of_loss
                  ? React.createElement("span", null,
                      r.state_of_loss,
                      React.createElement("span", {
                        style: { color: C.textMuted, fontSize: "12px", marginLeft: "8px" },
                      }, r.state_source === "app" ? "recorded here" : "from Clio"))
                  // The firm works in five states, so five buttons beats
                  // a dropdown: one click instead of three.
                  : React.createElement("span", {
                      style: { display: "flex", gap: "4px", flexWrap: "wrap" },
                    },
                      states.map(code => React.createElement("button", {
                        key: code,
                        disabled: saving[r.matter_id],
                        onClick: () => setState(r, code),
                        style: {
                          padding: "3px 9px", fontSize: "12px", cursor: "pointer",
                          background: "transparent", color: C.navy,
                          border: `1px solid ${C.warmGray}`, borderRadius: "4px",
                        },
                      }, code)))),
            )))),
        ),

    React.createElement("p", {
      style: { fontSize: "12px", color: C.textMuted, marginTop: "16px", lineHeight: 1.6 },
    }, "The state of loss is where the incident happened, which is not "
     + "necessarily where the client lives. It is recorded here rather than "
     + "written back to Clio."),
  );
}
